import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { usePartnerAccount } from '../../providers/PartnerAccount.provider';
import { usePartnerAccountBusinessCase } from '../../providers/PartnerAccountsBusinessCase.provider';
import { useHubSpotData } from '../../providers/HubSpotData.provider';
import { useConfirmationDialogue } from '../../providers/ConfirmationDialogue.provider';
import { usePendingData } from '../../providers/PendingData.provider';
import { usePartnerLogin } from '../../providers/PartnerLogin.provider';
import { useCompanyProfileFields } from '../../providers/CompanyProfileFields.provider';
import { useDocument } from '../../providers/Document.provider';
import { usePartnerAccountMatrix } from '../../providers/PartnerAccountsMatrix.provider';
import useFetchHubSpotDealDetails from '../useApi/useFetchHubSpotDealDetails';

const usePostPartnerAccountBusinessCaseDialogue = (): {
  destination?: string;
  navigate: () => void;
  showDialogue: () => void;
} => {
  const history = useHistory();
  const [destination, setDestination] = useState<string>();

  const { provideProps, showDialogue } = useConfirmationDialogue();
  const { reset: resetPendingData } = usePendingData();
  const { reset: resetPartnerAccount } = usePartnerAccount();
  const { response: partnerLoginResponse } = usePartnerLogin();
  const { isInsideSales } = partnerLoginResponse;
  const { response: businessCaseResponse, reset: resetBusinessCase } = usePartnerAccountBusinessCase();
  const { response: hubSpotDataResponse, reset: resetHubSpotData } = useHubSpotData();
  const { response: accountMatrixResponse, reset: resetAccountMatrix } = usePartnerAccountMatrix();
  const { response: documentUploadResponse, reset: resetDocumentUpload } = useDocument();
  const [, , , resetHubSpotDealData] = useFetchHubSpotDealDetails({});
  const { setMakeFieldsVisible, setFromReviewPage } = useCompanyProfileFields();

  const success =
    businessCaseResponse?.success &&
    (isInsideSales
      ? (hubSpotDataResponse?.success ?? true) &&
        (accountMatrixResponse?.success ?? true) &&
        documentUploadResponse?.success
      : true);
  const errorMessage =
    businessCaseResponse?.errorMessage ||
    (isInsideSales
      ? hubSpotDataResponse?.errorMessage || accountMatrixResponse?.errorMessage || documentUploadResponse?.errorMessage
      : '') ||
    '';

  const navigate = () => {
    if (destination) history.push(destination);
  };

  const handleReset = () => {
    resetPendingData();
    resetPartnerAccount();
    resetBusinessCase();
    resetHubSpotData();
    resetAccountMatrix();
    resetDocumentUpload();
    setMakeFieldsVisible(false);
    setFromReviewPage(false);
    resetHubSpotDealData();

    setDestination('/accounts');
  };

  useEffect(() => {
    const handlers = {
      default: () =>
        provideProps({
          title: 'Processing ...',
          subTitle: '',
          status: 'alert',
          confirm: {
            label: 'Please wait ...',
            action: () => {},
          },
        }),
      success: () =>
        provideProps({
          title: 'Success! Your account has been created.',
          subTitle: '',
          status: 'success',
          confirm: {
            label: 'Back to Accounts',
            action: () => handleReset(),
          },
        }),
      errorMessage: () => {
        provideProps({
          title: 'Error! Your account has been created. However, there was an error submitting the business case.',
          status: 'error',
          subTitle: errorMessage,
          confirm: {
            label: 'Back to Accounts',
            action: () => handleReset(),
          },
        });
      },
    };

    if (success) return handlers.success();

    if (typeof errorMessage !== 'undefined' && errorMessage !== '') return handlers.errorMessage();

    return handlers.default();
  }, [success, errorMessage]);

  return { destination, navigate, showDialogue };
};

export default usePostPartnerAccountBusinessCaseDialogue;
